/**
 * The delivered files, grouped the way the buyer will find them.
 *
 * The manifest is a flat list of paths because that is what a copy needs. A
 * person reviewing a release needs the opposite: the folders, opened one at a
 * time, with the files inside them. This does that grouping and nothing else,
 * so it can be checked without a browser.
 *
 * Paths come from `canonical_path` exactly as curation decided them. No
 * segment is invented here: a file that sits at the top of the package is
 * listed at the top, rather than under a synthetic root that no one chose.
 */

export interface ManifestEntry {
  manifestPath: string;
  sourceObjectKey: string;
  byteTotal: number;
  checksumSha256?: string;
}

export interface ManifestPage {
  entries: ManifestEntry[];
  /** Absent on the last page; its absence is the end of the tree. */
  nextAfterManifestPath?: string;
}

export interface FolderNode {
  name: string;
  path: string;
  files: ManifestEntry[];
  folders: FolderNode[];
  /** Every file at or below this folder. */
  objects: number;
  bytes: number;
}

export function readManifestPage(value: unknown): ManifestPage | null {
  const v = (value as { data?: unknown })?.data ?? value;
  if (!v || typeof v !== 'object') return null;
  const raw = (v as Record<string, unknown>).entries;
  if (!Array.isArray(raw)) return null;
  const cursor = (v as Record<string, unknown>).nextAfterManifestPath;
  return {
    entries: (raw as Record<string, unknown>[]).map((e) => ({
      manifestPath: String(e.manifestPath ?? ''),
      sourceObjectKey: String(e.sourceObjectKey ?? ''),
      byteTotal: typeof e.byteTotal === 'number' ? e.byteTotal : Number(e.byteTotal ?? 0) || 0,
      checksumSha256: typeof e.checksumSha256 === 'string' ? e.checksumSha256 : undefined,
    })),
    nextAfterManifestPath: typeof cursor === 'string' ? cursor : undefined,
  };
}

/** Groups a flat manifest into the folder tree the buyer opens. */
export function groupManifest(entries: ManifestEntry[]): FolderNode {
  const root: FolderNode = { name: '', path: '', files: [], folders: [], objects: 0, bytes: 0 };
  for (const entry of entries) {
    const segments = entry.manifestPath.split('/').filter(Boolean);
    // The last segment is the file; everything before it is a folder that
    // already exists in the decided path.
    const fileName = segments.pop();
    if (!fileName) continue;
    let node = root;
    node.objects += 1;
    node.bytes += entry.byteTotal;
    let path = '';
    for (const segment of segments) {
      path = `${path}${segment}/`;
      let next = node.folders.find((f) => f.name === segment);
      if (!next) {
        next = { name: segment, path, files: [], folders: [], objects: 0, bytes: 0 };
        node.folders.push(next);
      }
      next.objects += 1;
      next.bytes += entry.byteTotal;
      node = next;
    }
    node.files.push(entry);
  }
  return root;
}

/** The file's own name, for a row that already sits inside its folder. */
export function fileName(entry: ManifestEntry): string {
  const segments = entry.manifestPath.split('/').filter(Boolean);
  return segments[segments.length - 1] ?? entry.manifestPath;
}
